"use client";

import { m as motion, useReducedMotion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Rocket,
  FileCode2,
  GitBranch,
  Cloud,
  BookOpen,
  LifeBuoy,
  Package,
  type LucideIcon,
} from "lucide-react";

interface MvpDeliverablesProps {
  isItalian: boolean;
}

type Deliverable = {
  icon: LucideIcon;
  title: string;
  detail: string;
};

const EASE = [0.16, 1, 0.3, 1] as const;

const TECH = [
  "Next.js",
  "React",
  "TypeScript",
  "Tailwind CSS",
  "Supabase",
  "PostgreSQL",
  "Vercel",
  "AWS",
  "Flutter",
];

/**
 * "Cosa ricevi": i 6 deliverable come elenco visivo (non check-grid), piu' lo
 * stack come marquee animato in dissolvenza (non tag statici).
 *
 * Le card hanno un leggero "flip" (rotateY) che gira ogni tanto, sfalsato tra
 * una card e l'altra (non tutte insieme), solo mentre la sezione e' in viewport.
 * useReducedMotion -> marquee fermo, card statiche e visibili subito.
 */
export function MvpDeliverables({ isItalian }: MvpDeliverablesProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  // Gate per il flip in loop: continuo, si spegne fuori dal viewport.
  const inViewLoop = useInView(ref, { margin: "-60px" });
  const show = inView || reduce;
  const flipActive = inViewLoop && !reduce;

  const deliverables: Deliverable[] = isItalian
    ? [
        {
          icon: Rocket,
          title: "App live deployata",
          detail: "Web app funzionante, online e raggiungibile dai tuoi utenti.",
        },
        {
          icon: FileCode2,
          title: "Codebase pulita",
          detail: "Codice ordinato e documentato, facile da riprendere e far crescere.",
        },
        {
          icon: GitBranch,
          title: "Repo Git + CI/CD",
          detail: "Repository tuo con deploy automatici a ogni rilascio.",
        },
        {
          icon: Cloud,
          title: "Infra cloud",
          detail: "Infrastruttura configurata e pronta a scalare quando serve.",
        },
        {
          icon: BookOpen,
          title: "Passaggio di consegne",
          detail: "Documentazione e consegna tecnica completa, niente scatole chiuse.",
        },
        {
          icon: LifeBuoy,
          title: "30 giorni di supporto",
          detail: "Affianco post-lancio per i primi giorni con utenti reali.",
        },
      ]
    : [
        {
          icon: Rocket,
          title: "Deployed live app",
          detail: "A working web app, online and reachable by your users.",
        },
        {
          icon: FileCode2,
          title: "Clean codebase",
          detail: "Tidy, documented code that's easy to pick up and grow.",
        },
        {
          icon: GitBranch,
          title: "Git repo + CI/CD",
          detail: "Your own repository with automatic deploys on every release.",
        },
        {
          icon: Cloud,
          title: "Cloud infra",
          detail: "Infrastructure configured and ready to scale when needed.",
        },
        {
          icon: BookOpen,
          title: "Technical handover",
          detail: "Full documentation and handover, no black boxes.",
        },
        {
          icon: LifeBuoy,
          title: "30 days of support",
          detail: "I stay alongside you for the first days with real users.",
        },
      ];

  // Track duplicato per marquee continuo (transform-only).
  const marqueeTrack = [...TECH, ...TECH];

  return (
    <section className="section-padding bg-surface/50">
      <div className="container-wide" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={show ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-left mb-12 max-w-2xl"
        >
          <span className="inline-flex items-center gap-2 font-mono text-label text-primary mb-3">
            <Package className="w-4 h-4" aria-hidden="true" />
            {isItalian ? "Tutto quello che ricevi" : "Everything you get"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? "Cosa ricevi" : "What you get"}
          </h2>
          <p className="text-body-lg text-muted-foreground">
            {isItalian
              ? "Non solo codice, ma un prodotto completo e tutto quello che serve per gestirlo in autonomia."
              : "Not just code, but a complete product and everything you need to run it on your own."}
          </p>
        </motion.div>

        {/* 6 deliverable con leggero flip rotateY in loop sfalsato */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {deliverables.map((d, i) => (
            <motion.div
              key={d.title}
              initial={false}
              animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{
                duration: reduce ? 0 : 0.45,
                delay: reduce ? 0 : i * 0.08,
                ease: EASE,
              }}
              style={{ perspective: 800 }}
            >
              <motion.div
                initial={false}
                animate={flipActive ? { rotateY: [0, 0, -12, 0] } : { rotateY: 0 }}
                transition={
                  flipActive
                    ? {
                        duration: 6,
                        ease: "easeInOut",
                        repeat: Infinity,
                        // Sfasamento: ogni card parte in un momento diverso.
                        delay: i * 0.9,
                        times: [0, 0.55, 0.72, 0.9],
                      }
                    : { duration: 0.4 }
                }
                style={{ transformStyle: "preserve-3d" }}
                className="surface-card p-5 flex gap-4 items-start h-full"
              >
                <span className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                  <d.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold mb-1">
                    {d.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {d.detail}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Stack tecnologico: marquee animato in dissolvenza + timeline */}
        <div className="surface-card p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="shrink-0 text-left">
              <p className="font-mono text-sm text-muted-foreground mb-1">
                {isItalian ? "Timeline tipica" : "Typical timeline"}
              </p>
              <p className="font-display text-3xl font-bold text-primary">
                4-6 {isItalian ? "settimane" : "weeks"}
              </p>
            </div>

            <div className="hidden md:block w-px self-stretch bg-[hsl(var(--border))]" />

            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground mb-3 text-left">
                {isItalian ? "Stack tecnologico" : "Tech stack"}
              </p>

              {reduce ? (
                // Reduced motion: stack statico, niente marquee.
                <div className="flex flex-wrap gap-2 justify-start">
                  {TECH.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1.5 rounded-lg bg-background/60 text-sm font-mono text-muted-foreground border border-[hsl(var(--border))]"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              ) : (
                <div
                  className="relative overflow-hidden"
                  style={{
                    maskImage:
                      "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
                    WebkitMaskImage:
                      "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
                  }}
                >
                  <motion.div
                    className="flex gap-2 w-max"
                    animate={show ? { x: ["0%", "-50%"] } : { x: "0%" }}
                    transition={{
                      duration: 22,
                      ease: "linear",
                      repeat: Infinity,
                    }}
                  >
                    {marqueeTrack.map((tech, i) => (
                      <span
                        key={`${tech}-${i}`}
                        className="px-3 py-1.5 rounded-lg bg-background/60 text-sm font-mono text-muted-foreground border border-[hsl(var(--border))] whitespace-nowrap"
                      >
                        {tech}
                      </span>
                    ))}
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
